import { App, Button, Form, Input } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ModalLayout from '../../layouts/ModalLayout';
import { UserService } from '../../services/userApiService';
import { getApiErrorDetail } from '../../utils/apiErrorCode';

const userService = new UserService();
const PASSKEY_EDIT_FORM_ID = 'passkey-edit-form';

export interface PasskeyEditFormProps {
  credentialId: string;
  initialDeviceName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PasskeyEditFormValues {
  deviceName: string;
}

/**
 * A form for renaming a single passkey (device display name).
 * - Validates device name
 * - Calls the UserService to rename
 * - Shows notification and handles busy state
 */
export default function PasskeyEditForm({
  credentialId,
  initialDeviceName,
  onClose,
  onSuccess,
}: PasskeyEditFormProps) {
  const { t } = useTranslation();
  // Ant Design message API
  const { message } = App.useApp();
  // Form instance for Antd Form
  const [form] = Form.useForm<PasskeyEditFormValues>();
  // Indicates if the save operation is in progress
  const [saveBusy, setSaveBusy] = useState(false);

  /**
   * Validation rules for deviceName:
   * - Required
   * - Min length 1, Max length 100
   */
  const deviceNameRules = useMemo(
    () => [
      {
        validator: async (_: unknown, value: string | undefined) => {
          const trimmed = (value ?? '').trim();
          if (trimmed.length < 1 || trimmed.length > 100) {
            // Localized validation message
            return Promise.reject(new Error(t('modal.passkeyEditForm.deviceNameInvalid')));
          }
          return Promise.resolve();
        },
      },
    ],
    [t],
  );

  /**
   * Handles form submission for renaming passkey device.
   * - Trims device name
   * - Sets loading state
   * - Calls backend API
   * - Shows success/error notification
   * - Triggers optional onSuccess callback
   */
  const handleFinish = useCallback(
    async (values: PasskeyEditFormValues) => {
      const trimmed = values.deviceName.trim();
      setSaveBusy(true);
      try {
        // Call user service to rename passkey device
        await userService.passkeyRename(credentialId, { deviceName: trimmed });
        // Success notification
        message.success(t('modal.passkeyEditForm.renameSuccess'));
        // Invoke onSuccess callback if provided
        onSuccess?.();
      } catch (error: unknown) {
        // Show error from API or fallback translation
        message.error(getApiErrorDetail(error) ?? t('modal.passkeyEditForm.renameError'));
      } finally {
        // Reset busy state
        setSaveBusy(false);
      }
    },
    [credentialId, message, onSuccess, t],
  );

  return (
    <ModalLayout>
      {/* Modal Content: passkey rename form */}
      <ModalLayout.Content>
        <div className="space-y-3 pt-2">
          <Form<PasskeyEditFormValues>
            id={PASSKEY_EDIT_FORM_ID}
            form={form}
            initialValues={{ deviceName: initialDeviceName }}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => void handleFinish(values)}
          >
            {/* Form field for device name */}
            <Form.Item name="deviceName" rules={deviceNameRules}>
              <Input maxLength={100} placeholder={t('modal.passkeyEditForm.deviceNameLabel')} />
            </Form.Item>
          </Form>
        </div>
      </ModalLayout.Content>
      {/* Modal Footer: action buttons */}
      <ModalLayout.Footer>
        <div className="flex justify-end gap-2">
          {/* Cancel button closes the modal */}
          <Button htmlType="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* Save button submits the form */}
          <Button form={PASSKEY_EDIT_FORM_ID} htmlType="submit" loading={saveBusy} type="primary">
            {t('modal.passkeyEditForm.saveLabel')}
          </Button>
        </div>
      </ModalLayout.Footer>
    </ModalLayout>
  );
}
